<template>
  <div>
    <div v-if="scanner && items.length">
      <div class="page-head">
        <div>
          <h3>Order Barcode Scanner</h3>
          <p class="page-head-sub">Scan each part of the Regos invoice</p>
        </div>
      </div>
      <div class="toolbar">
      <div class="toolbar-field" style="max-width: 360px">
        <b-form-input
          class="bg-dark text-white"
          ref="ean"
          v-model="code"
          v-on:keyup.enter="scan"
          placeholder="Scan EAN code"></b-form-input>
      </div>
      <button @click="closeScanner" class="btn btn-danger px-4">
        <b-icon-x-circle class="mr-2"></b-icon-x-circle>Close scanner
      </button>
      </div>
    </div>
    <div v-else>
      <div class="page-head">
        <div>
          <h3>Regos Parts</h3>
          <p class="page-head-sub">Parts on a Regos invoice and their Poland orders</p>
        </div>
      </div>
      <div class="toolbar">
      <div class="toolbar-field">
        <label>Regos ID</label>
        <b-form-input
          style="height: 42px;"
          v-model.number="regosID"
          type="number"
          v-on:keyup.enter="getParts"
          class="bg-dark text-white"></b-form-input>
      </div>
      <button @click="getParts" :class="!regosID ? 'btn-secondary disabled' : 'btn-primary'" class="btn px-4">
        <b-icon-search class="mr-2"></b-icon-search>Search
      </button>
      <div class="toolbar-field">
        <label>Box</label>
        <b-form-select
          style="height: 42px;"
          class=""
          placeholder="Select Box"
          v-model="location"
          :disabled="!data.regosItems.length"
          :options="shelfBoxes"></b-form-select>
      </div>
      <button @click="startScanner" :class="!data.regosItems.length || !location ? 'btn-secondary disabled' : 'btn-success'" class="btn px-4">
        <b-icon-upc-scan class="mr-2"></b-icon-upc-scan>Scanner
      </button>
      </div>
    </div>
    <div class="w-100 position-relative" v-if="loading">
      <div class="w-100 mt-5 position-absolute text-center">
        <b-icon class="text-dark" icon="arrow-clockwise" animation="spin" font-scale="3"></b-icon>
      </div>
    </div>

    <div v-if="items.length">
      <div class="row mx-0">
        <table class="table" ref="articles">
          <thead>
          <tr>
            <th scope="col">#</th>
            <th scope="col">Updated</th>
            <th scope="col">SKU</th>
            <th class="d-none d-sm-table-cell" scope="col">Name</th>
            <th class="d-none d-sm-table-cell" scope="col">Ordered</th>
            <th class="d-none d-sm-table-cell text-center" scope="col">Local</th>
            <th class="d-none d-sm-table-cell" scope="col">Scanned</th>
            <th class="d-none d-sm-table-cell text-center" scope="col">Box</th>
            <th class="d-none d-sm-table-cell" scope="col">Reduce</th>
          </tr>
          </thead>
          <tbody>
            <tr v-for="(val, i) of items" v-bind:key="i" :class="[val.count === val.scanned ? 'bg-success' : 'bg-warning', 'text-dark']">
              <td :style="scanned && scanned === val.product.codename ? 'background: var(--accent); color: #ffffff' : ''">{{i+1}}</td>
              <td>{{moment(val.updatedAt).format('DD.MM.YYYY HH:mm')}}</td>
              <td>{{val.product.codename}}</td>
              <td @click="put(val._id)" class="cursor-pointer">{{val.product.name}}</td>
              <td class="text-center">{{val.count}}</td>
              <td class="text-center"><strong>{{val.local || 0}}</strong> {{val.product.localLocation ? `[${val.product.localLocation}]` : ''}}</td>
              <td class="text-center">{{val.scanned || 0}}</td>
              <td class="text-center">{{val.location || 'Not in box'}}</td>
              <td class="text-center">
                <b-button class="btn-sm" @click="backToStock(val)" :disabled="!val.scanned">
                  <b-icon-chevron-left></b-icon-chevron-left>
                </b-button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-if="data.orderedItems && data.orderedItems.length" class="row mx-0">
        <h5 class="section-label w-100"><span class="dot bg-primary"></span>Poland orders for {{regosID}}</h5>
        <table class="table">
          <thead>
          <tr>
            <th class="d-none d-sm-table-cell" scope="col">SKU</th>
            <th class="d-none d-sm-table-cell" scope="col">Name</th>
            <th class="d-none d-sm-table-cell" scope="col">Ordered</th>
            <th class="d-none d-sm-table-cell" scope="col">Packed</th>
            <th class="d-none d-sm-table-cell" scope="col">Sent</th>
            <th class="d-none d-sm-table-cell" scope="col">Received</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(row, i) of data.orderedItems" v-bind:key="i" :class="getRowClass(row)">
            <td v-b-tooltip.hover.left="row.noteType ? stripHtml(row.notes) : ''">{{row.sku}}</td>
            <td>{{row.name}}</td>
            <td>{{moment(row.addedAt).format('DD.MM.YYYY')}}</td>
            <td>{{row.packedAt ? moment(row.packedAt).format('DD.MM.YYYY'): 'N/A'}}</td>
            <td>{{row.transport && row.transport.poland ? moment(row.transport.poland).format('DD.MM.YYYY') : 'N/A'}}</td>
            <td>{{row.transport && row.transport.norway ? moment(row.transport.norway).format('DD.MM.YYYY') : 'N/A'}}</td>
          </tr>
          </tbody>
        </table>
      </div>
      <div v-else class="empty-state">
        No products from Poland for selected Regos ID
      </div>
    </div>
    <div v-else class="empty-state">
      <b-icon-card-list></b-icon-card-list>
      Enter a Regos ID to get the parts list
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';

import fixed from '@/fixed';
import { stripHtmlTags, getRowClass } from '@/utils';

export default {
  name: 'RegosParts.vue',
  data: () => ({
    loading: false,
    regosID: null,
    location: null,
    scanner: false,
    code: '',
    scanned: '',
    ticker: null,
    data: { regosItems: [], orderedItems: [] },
    shelfBoxes: [{ value: null, text: 'Select Box', disabled: true }],

    moment,
  }),
  mounted() {
    this.getParts();
    this.generateBoxes();
  },
  methods: {
    // Yellow: we refused on purpose, the message says why. Red: something broke.
    showFailure(error, action) {
      const { status, data } = error.response || {};
      if (status === 401) return; // the login redirect in http.js takes over

      const reason = typeof data === 'string' && data ? data : error.message;
      if (status === 403) {
        this.$toast.warning('You\'re not authorised for this action ✋🏻');
      } else if (status === 400) {
        this.$toast.warning(reason);
      } else {
        this.$toast.error(`Error while ${action}: ${reason}`);
      }
    },
    startScanner() {
      if (!this.data.regosItems.length || !this.location) return;

      this.scanner = true;
      this.ticker = setInterval(() => {
        this.$refs.ean.focus();
      }, 1000);
    },
    closeScanner() {
      if (this.ticker) clearInterval(this.ticker);
      this.scanned = '';
      this.scanner = false;
    },
    async getParts() {
      if (!this.regosID) return;

      this.loading = true;
      try {
        const { data } = await axios.get(`${fixed.API_URL}/admin/w-items-regos`, { params: { id: this.regosID }, withCredentials: true });
        if (!data || !data.regosItems || !data.regosItems.length) {
          this.$toast.info('No parts for that Regos ID');
          this.data = { regosItems: [], orderedItems: [] };
        } else {
          this.data = data;
        }
      } catch (error) {
        this.showFailure(error, 'fetching Regos parts');
      }
      this.loading = false;
    },
    async scan() {
      if (!this.code) {
        this.$toast.warning('Scan an article code first');
      } else {
        // Update params
        const payload = {
          regosID: this.regosID,
          code: this.code,
          location: this.location,
        };

        try {
          const { data } = await axios.put(
            `${fixed.API_URL}/admin/scan-w-item`, payload,
            { withCredentials: true },
          );
          this.scanned = data.product.codename;
          await this.getParts();
        } catch (error) {
          this.scanned = '';
          this.showFailure(error, 'scanning the article');
        }
      }
      this.code = '';
    },
    async put(id) {
      if (!id) return;

      const ok = confirm('Are you sure you want to confirm one of these?'); // eslint-disable-line
      if (!ok) return;

      this.loading = true;
      try {
        await axios.put(`${fixed.API_URL}/admin/put-w-item?id=${id}`, {}, { withCredentials: true });
        await this.getParts();
      } catch (error) {
        this.showFailure(error, 'confirming the part');
      }
      this.loading = false;
    },
    async backToStock(item) {
      if (!item?.product?.codename || !this.regosID) return;

      if (item.scanned < 1) {
        this.$toast.warning('Only parts scanned into the Regos room can be returned');
        return;
      }

      this.loading = true;
      try {
        const resp = await axios.put(`${fixed.API_URL}/admin/w-item`,
          { regosID: this.regosID, code: item.product.codename },
          { withCredentials: true });

        this.$toast.success(resp.data || 'Item returned successfully');
      } catch (error) {
        this.showFailure(error, 'returning the part');
      }
      await this.getParts();
      this.loading = false;
    },
    generateBoxes() {
      for (let i = 1; i <= 50; i += 1) {
        this.shelfBoxes.push({ value: `Box${i}`, text: `Box ${i}` });
      }
    },
    stripHtml(input) {
      return stripHtmlTags(input);
    },
    getRowClass(row) {
      return getRowClass(row);
    },
  },
  computed: {
    items() {
      return this.data.regosItems.filter((item) => !item.removed);
    },
  },
};
</script>

<style scoped>
.cursor-pointer{
  cursor: pointer;
}
</style>
